import epicenterSvg from "@/assets/img/svg/epicenter.svg";
import staTriangleSvg from "@/assets/img/svg/stationTriangle.svg?raw";
import * as d3 from "d3";
import L from "leaflet";
// import { divIcon, icon } from "leaflet";

//==palert資料開放起始日
export const PALERT_OPEN_DATE = "2012-04-27";

export const getImage = (imgPath) => {
  //   console.debug(img);
  return new URL(`/src/assets/img/${imgPath}`, import.meta.url).href;
};
export const getSvgData = (svgRaw, color, opacity = 0.5) => {
  //in URLs starts a fragment identifier. So, in order to make that work, write %23 instead of #. That is the value of escaped # character.
  color = color.replace("#", "%23");
  // console.debug(svgRaw);
  const svgString = svgRaw
    .replaceAll("${color}", color)
    .replace("${opacity}", opacity);

  return "data:image/svg+xml," + svgString;
};
export const getLeafletIcon = (config) => {
  let iconSvg = config.icon,
    iconSize = config.size,
    color = config.color,
    opacity = config.opacity,
    className = config.className;

  let leafletIcon;
  switch (iconSvg) {
    case "epicenter":
      leafletIcon = L.icon({
        iconUrl: epicenterSvg,
        iconSize: [iconSize, iconSize],
      });
      break;
    default:
      leafletIcon = L.divIcon({
        className: className,
        html: `
          <img     
            src='${getSvgData(staTriangleSvg, color, opacity)}'
            style='
              position: absolute;
              width: ${iconSize * 2}px;
              height: ${iconSize * 2}px;
            '>`,
        iconSize: [iconSize * 2, iconSize * 2],
        iconAnchor: [iconSize, iconSize],
      });
      break;
  }
  // console.debug(leafletIcon);
  return leafletIcon;
};

//==legend
export const LegendScope = {
  ml: {
    domain: [4, 8], //==規模範圍[3,7]
    get range() {
      //==[4, 5, 6, 7, 8]
      return d3.range(...this.domain).concat(this.domain[1]);
    },
  },
  depth: {
    domain: [0, 320], //==深度範圍
  },
  pga: {
    domain: [0.8, 2.5, 8, 25, 80, 140, 250, 440, 800], //==pga threshold
    range: [
      //==pga color
      "rgb(255, 255, 255)",
      "rgb(200, 255, 211)",
      "rgb(28, 255, 28)",
      "rgb(255, 255, 0)",
      "rgb(255, 170, 0)",
      "rgb(255, 90, 0)",
      "rgb(209, 54, 15)",
      "rgb(161, 52, 35)",
      "rgb(153, 12, 51)",
      "rgb(153, 41, 165)",
    ],
  },
  pgv: {
    domain: [0.2, 0.7, 1.9, 5.7, 15, 30, 50, 80, 140], //==pgv threshold
    range: [
      "rgb(255, 255, 255)",
      "rgb(200, 255, 211)",
      "rgb(28, 255, 28)",
      "rgb(255, 255, 0)",
      "rgb(255, 170, 0)",
      "rgb(255, 90, 0)",
      "rgb(209, 54, 15)",
      "rgb(161, 52, 35)",
      "rgb(153, 12, 51)",
      "rgb(153, 41, 165)",
    ],
  },
};

export const getMLScale = () => {
  return (ml, circleSize) => {
    // console.debug(ml);
    let ml_base = 3;
    return ml > ml_base ? (ml - ml_base) * circleSize + 0.1 : 0.1;
  };
};

export const getDataScale = (key) => {
  let legend = LegendScope[key];
  let domain = legend.domain,
    range = legend.range;
  return d3.scaleThreshold().domain(domain).range(range);
};

export const getDepthScale = (
  reverse = true,
  depthDomain = LegendScope.depth.domain
) => {
  return d3.scaleSequentialSqrt(
    reverse ? [...depthDomain].reverse() : [...depthDomain],
    d3.interpolateTurbo
  );
};

//==@d3/color-legend
export const getColorLegend = ({
  color,
  title,
  tickSize = 6,
  width = 320,
  height = 44 + tickSize,
  marginTop = 18,
  marginRight = 0,
  marginBottom = 16 + tickSize,
  marginLeft = 0,
  ticks = width / 64,
  tickFormat = undefined,
  tickValues,
  vertical = false,
  reverseColor = false,
} = {}) => {
  function ramp(color, n = 256) {
    const canvas = document.createElement("canvas");
    canvas.width = vertical ? 1 : n;
    canvas.height = vertical ? n : 1;
    const context = canvas.getContext("2d");
    for (let i = 0; i < n; ++i) {
      context.fillStyle = color(i / (n - 1));
      context.fillRect(...(vertical ? [0, i, 1, 1] : [i, 0, 1, 1]));
    }
    return canvas;
  }

  const svg = d3
    .create("svg")
    .attr("width", vertical ? height : width)
    .attr("height", vertical ? width : height)
    .attr("viewBox", vertical ? [0, 0, height, width] : [0, 0, width, height])
    .style("overflow", "visible")
    .style("display", "block");

  let tickAdjust = (g) =>
    g
      .selectAll(".tick line")
      .attr("x1", vertical ? -tickSize : undefined)
      .attr("y1", vertical ? undefined : marginTop + marginBottom - height);
  let x;

  // Continuous
  if (color.interpolate) {
    const n = Math.min(color.domain().length, color.range().length);

    x = color
      .copy()
      .rangeRound(
        d3.quantize(d3.interpolate(marginLeft, width - marginRight), n)
      );

    svg
      .append("image")
      .attr("x", marginLeft)
      .attr("y", marginTop)
      .attr("width", width - marginLeft - marginRight)
      .attr("height", height - marginTop - marginBottom)
      .attr("preserveAspectRatio", "none")
      .attr(
        "xlink:href",
        ramp(
          color.copy().domain(d3.quantize(d3.interpolate(0, 1), n))
        ).toDataURL()
      );
  }

  // Sequential
  else if (color.interpolator) {
    x = Object.assign(
      color
        .copy()
        .interpolator(d3.interpolateRound(marginLeft, width - marginRight)),
      {
        range() {
          return [marginLeft, width - marginRight];
        },
      }
    );

    let colorBar = svg.append("image").call((g) => {
      if (vertical)
        g.attr("x", marginTop)
          .attr("y", marginLeft)
          .attr("width", height - marginTop - marginBottom)
          .attr("height", width - marginLeft - marginRight);
      else
        g.attr("x", marginLeft)
          .attr("y", marginTop)
          .attr("width", width - marginLeft - marginRight)
          .attr("height", height - marginTop - marginBottom);

      g.attr("preserveAspectRatio", "none").attr(
        "xlink:href",
        ramp(color.interpolator()).toDataURL()
      );
    });
    if (reverseColor)
      colorBar.attr(
        "transform",
        vertical
          ? `translate(0,${width - marginLeft - marginRight}) scale(1,-1)`
          : `translate(${width - marginLeft - marginRight}) scale(-1,1)`
      );

    // scaleSequentialQuantile doesn’t implement ticks or tickFormat.
    if (!x.ticks) {
      if (tickValues === undefined) {
        const n = Math.round(ticks + 1);
        tickValues = d3
          .range(n)
          .map((i) => d3.quantile(color.domain(), i / (n - 1)));
      }
      if (typeof tickFormat !== "function") {
        tickFormat = d3.format(tickFormat === undefined ? ",f" : tickFormat);
      }
    }
  }

  // Threshold
  else if (color.invertExtent) {
    const thresholds = color.thresholds
      ? color.thresholds() // scaleQuantize
      : color.quantiles
      ? color.quantiles() // scaleQuantile
      : color.domain(); // scaleThreshold

    const thresholdFormat =
      tickFormat === undefined
        ? (d) => d
        : typeof tickFormat === "string"
        ? d3.format(tickFormat)
        : tickFormat;

    x = d3
      .scaleLinear()
      .domain([-1, color.range().length - 1])
      .rangeRound([marginLeft, width - marginRight]);

    svg
      .append("g")
      .selectAll("rect")
      .data(color.range())
      .join("rect")
      .call((g) => {
        if (vertical)
          g.attr("x", marginTop)
            .attr("y", (d, i) => x(color.range().length - i - 2))
            .attr("width", height - marginTop - marginBottom)
            .attr("height", (d, i) => x(i) - x(i - 1))
            .attr("fill", (d) => d);
        else
          g.attr("x", (d, i) => x(i - 1))
            .attr("y", marginTop)
            .attr("width", (d, i) => x(i) - x(i - 1))
            .attr("height", height - marginTop - marginBottom)
            .attr("fill", (d) => d);
      });

    tickValues = d3.range(thresholds.length);
    tickFormat = (i) =>
      thresholdFormat(thresholds[vertical ? thresholds.length - 1 - i : i], i);
  }

  // Ordinal
  else {
    x = d3
      .scaleBand()
      .domain(color.domain())
      .rangeRound([marginLeft, width - marginRight]);

    svg
      .append("g")
      .selectAll("rect")
      .data(color.domain())
      .join("rect")
      .attr("x", x)
      .attr("y", marginTop)
      .attr("width", Math.max(0, x.bandwidth() - 1))
      .attr("height", height - marginTop - marginBottom)
      .attr("fill", color);

    tickAdjust = () => {};
  }
  // console.debug(tickFormat);
  svg
    .append("g")
    .attr(
      "transform",
      `translate(${vertical ? [marginTop + 5, 0] : [0, height - marginBottom]})`
    )
    .call(
      d3[vertical ? "axisRight" : "axisBottom"](x)
        .ticks(ticks, typeof tickFormat === "string" ? tickFormat : undefined)
        .tickFormat(typeof tickFormat === "function" ? tickFormat : undefined)
        .tickSize(tickSize)
        .tickValues(tickValues)
    )
    .call(tickAdjust)
    .call((g) => g.select(".domain").remove())
    .call((g) =>
      g
        .append("text")
        .attr("x", vertical ? -marginTop - marginBottom : marginLeft)
        .attr(
          "y",
          vertical ? -marginTop + 6 : marginTop + marginBottom - height - 6
        )
        .attr("fill", "currentColor")
        .attr("text-anchor", "start")
        .attr("font-weight", "bold")
        .attr("class", "title")
        .text(title)
    );

  return svg.node();
};

//==減少事件...觸發頻率
export const updateHandler = (
  action,
  updateObj = { updateFlag: true, updateTimeOut: null, updateDelay: 10 },
  mustDone = false
) => {
  // console.debug(parameter)
  // console.debug(chartUpdateObj.updateFlag);

  if (!updateObj.updateFlag) clearTimeout(updateObj.updateTimeOut);

  updateObj.updateTimeOut = setTimeout(() => {
    action.param ? action.fun(...action.param) : action();
    updateObj.updateFlag = true;
  }, updateObj.updateDelay);

  updateObj.updateFlag = mustDone;
};

//===資料篩選相關

// 判斷資料是否在範圍內的函數
export const getIsInRangeFun = (range, maxRange = 360) => {
  let fun;
  // console.debug(range, maxRange);

  // location一開始是空
  if (range.length === 0) {
    fun = (x) => true;
  }
  // az能用負角度篩選
  else if (range[0] < 0) {
    fun = (x) => x >= range[0] + maxRange || x <= range[1];
  } else {
    fun = (x) =>
      (!isNaN(range[0]) ? x >= range[0] : true) &&
      (!isNaN(range[1]) ? x <= range[1] : true);
  }
  return fun;
};

// az,dist在地圖上的遮罩圖形
export const FilterShape = {
  shapeColor: "grey",
  createMask: (obj) => {
    // console.debug("createMask", obj);
    let shape = obj._path;
    let svg = shape.parentNode.parentNode;

    //==建立範圍圖形g
    if (!svg.querySelector("g.filterShape"))
      svg.insertAdjacentHTML(
        "afterbegin",
        `<g class="filterShape">
        <defs>
            <mask id="filterMask">
            </mask>
        </defs>
      </g>
      `
      );
    let g = svg.querySelector("g.filterShape");
    //顯示圖形
    if (obj.options.color === FilterShape.shapeColor) {
      shape.setAttribute("mask", "url(#filterMask)");
      shape.setAttribute("id", "rangeShape");
      g.append(shape);
    }
    //圖形遮罩
    else {
      g.querySelector("defs>#filterMask").append(shape);
    }
    // console.debug(obj, defsMask);
  },
  getPolygonPoint: (lat, lon, dist, az) => {
    let points = [];
    // 轉弧度係數
    const arc = Math.PI / 180;
    //外圓x倍半徑
    let r = dist / 80;

    //兩倍圓內接正方形頂點(角度45,135,225,315)
    let corner = [
      [lat + r, lon + r],
      [lat - r, lon + r],
      [lat - r, lon - r],
      [lat + r, lon - r],
    ];

    //圓心
    let centre = [lat, lon];

    let startAngle = 90 - az[0],
      endAngle = 90 - az[1];

    let startLine = [
        lat + Math.sin(startAngle * arc) * r,
        lon + Math.cos(startAngle * arc) * r,
      ],
      endLine = [
        lat + Math.sin(endAngle * arc) * r,
        lon + Math.cos(endAngle * arc) * r,
      ];

    //==1.算出需要遮罩的角度再除90算需要經過幾個頂點(最少1個)
    let cornerPassThruCount = Math.floor((360 - (az[1] - az[0])) / 90) + 1;
    //==2.從結束角度開始順時針繞過第一步算的需要經過頂點數
    let cornerPassThru = Array.from(Array(cornerPassThruCount).keys()).map(
      (i) => {
        let startIdx = Math.ceil((az[1] + 45) / 90) - 1;
        return corner[(startIdx + i) % 4];
      }
    );
    // console.debug(cornerPassThru);

    points.push(centre, endLine, ...cornerPassThru, startLine, centre);
    return points;
  },
};
